/**
 * Reset-password page (USR-007) — step 2 of the reset flow.
 *
 * Opened from the link in the reset e-mail (`/reset?token=…`, built by the
 * API from `BRAND.baseUrl`). The client picks a new password with the same
 * rules as at registration (≥8 characters, one uppercase letter, one digit;
 * the API enforces them again). On success the API has revoked every
 * session of the account, so the page sends the client to the login.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, confirmPasswordReset } from '@/lib/api-client';

type Outcome = 'idle' | 'done' | 'invalid-token';

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [outcome, setOutcome] = useState<Outcome>(token ? 'idle' : 'invalid-token');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = z
    .object({
      password: z
        .string()
        .min(1, t('login.errors.required'))
        .min(8, t('register.errors.passwordTooWeak'))
        .regex(/[A-Z]/, t('register.errors.passwordTooWeak'))
        .regex(/[0-9]/, t('register.errors.passwordTooWeak')),
      passwordConfirm: z.string().min(1, t('login.errors.required')),
    })
    .refine((data) => data.password === data.passwordConfirm, {
      path: ['passwordConfirm'],
      message: t('register.errors.passwordsDoNotMatch'),
    });
  type Fields = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', passwordConfirm: '' },
  });

  const onSubmit = async (data: Fields): Promise<void> => {
    setSubmitError(null);
    try {
      await confirmPasswordReset(token, data.password);
      setOutcome('done');
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        setOutcome('invalid-token');
      } else if (cause instanceof ApiError && cause.status === 400) {
        setSubmitError(t('register.errors.passwordTooWeak'));
      } else if (cause instanceof ApiError && cause.status === 429) {
        setSubmitError(t('passwordReset.errors.rateLimited'));
      } else {
        setSubmitError(t('login.errors.networkError'));
      }
    }
  };

  return (
    <section className="max-w-md mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('passwordReset.confirm.title')}</h1>
        {outcome === 'idle' && <p className="mt-s3 text-gray-2">{t('passwordReset.confirm.subtitle')}</p>}
      </header>

      {outcome === 'done' && (
        <div className="space-y-s4" data-testid="reset-done">
          <p role="status" className="text-ink">
            {t('passwordReset.confirm.done')}
          </p>
          <Link to="/login" className="btn-primary inline-block w-full text-center">
            {t('passwordReset.confirm.toLogin')}
          </Link>
        </div>
      )}

      {outcome === 'invalid-token' && (
        <div className="space-y-s4" data-testid="reset-invalid">
          <p role="alert" className="form-error">
            {t('passwordReset.errors.invalidToken')}
          </p>
          <Link to="/forgot-password" className="text-sm text-flame-ember hover:underline">
            {t('passwordReset.confirm.requestNew')}
          </Link>
        </div>
      )}

      {outcome === 'idle' && (
        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          noValidate
          className="space-y-s4"
        >
          <div>
            <label htmlFor="password" className="form-label">
              {t('passwordReset.confirm.password')}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder={t('register.placeholders.password')}
              className={`input-field ${errors.password ? 'input-error' : ''}`}
              {...register('password')}
            />
            {errors.password?.message && <p className="form-error">{errors.password.message}</p>}
          </div>

          <div>
            <label htmlFor="passwordConfirm" className="form-label">
              {t('register.fields.passwordConfirm')}
            </label>
            <input
              id="passwordConfirm"
              type="password"
              autoComplete="new-password"
              placeholder={t('register.placeholders.passwordConfirm')}
              className={`input-field ${errors.passwordConfirm ? 'input-error' : ''}`}
              {...register('passwordConfirm')}
            />
            {errors.passwordConfirm?.message && (
              <p className="form-error">{errors.passwordConfirm.message}</p>
            )}
          </div>

          {submitError && (
            <p role="alert" className="form-error" data-testid="submit-error">
              {submitError}
            </p>
          )}

          <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
            {isSubmitting ? t('passwordReset.confirm.submitting') : t('passwordReset.confirm.submit')}
          </button>
        </form>
      )}
    </section>
  );
}
